import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, MessageSquare, Mail, Eye, X, Zap, Target, TrendingUp, RefreshCw, Download } from 'lucide-react';
import api from '../../services/api';
import EnquiryDetailsModal from './EnquiryDetailsModal';
import FileViewerModal from '../common/FileViewerModal';

interface Enquiry {
  enquiry_id: number;
  source: 'whatsapp' | 'email' | 'agent_portal';
  agency_name: string;
  travel_date: string;
  plan_text: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'rework_requested';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  hours_in_pool: number;
  rework_count?: number;
  number_of_adults?: number;
  number_of_kids?: number;
  number_of_nights?: number;
  customer_name?: string;
  customer_reference?: string;
  pic_name?: string;
}

interface Rework {
  rework_id: number;
  enquiry_id: number;
  rework_number: number;
  changes_requested: string;
  pax_changed: boolean;
  new_adults?: number;
  new_kids?: number;
  date_changed: boolean;
  new_travel_date?: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed';
  created_at: string;
  agency_name: string;
  travel_date: string;
  plan_text: string;
  hours_in_pool: number;
  type: string;
  source: string;
  priority: string;
  selected_itinerary_id?: number;
  selected_itinerary_name?: string;
  attachment_file_path?: string;
  attachment_file_name?: string;
  rework_history?: ReworkHistoryItem[];
}

interface ReworkHistoryItem {
  rework_number: number;
  changes_requested: string;
  created_at: string;
  status: string;
  selected_itinerary_name?: string;
  selected_itinerary_id?: number;
  request_type: string;
}

const WorkPool: React.FC = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [reworks, setReworks] = useState<Rework[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [selectedRework, setSelectedRework] = useState<Rework | null>(null);
  const [reworkHistory, setReworkHistory] = useState<ReworkHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const [filePreview, setFilePreview] = useState<{ url: string; name: string; type: 'pdf' | 'image' } | null>(null);

  useEffect(() => {
    fetchEnquiries(); // This now fetches both enquiries and reworks
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/work-enquiries');
      console.log('Full API response:', response.data);
      
      if (response.data.success) {
        // Separate enquiries and reworks from unified response
        const allItems = response.data.data;
        console.log('All items received:', allItems);
        
        const pendingEnquiries = allItems.filter((item: any) => item.type === 'enquiry' && item.status === 'pending');
        const pendingReworks = allItems.filter((item: any) => item.type === 'rework' && item.status === 'pending');
        
        console.log('Filtered enquiries:', pendingEnquiries);
        console.log('Filtered reworks:', pendingReworks);
        
        setEnquiries(pendingEnquiries);
        setReworks(pendingReworks);
        setLastUpdated(Date.now());
        
        console.log('Fetched items:', { enquiries: pendingEnquiries.length, reworks: pendingReworks.length });
      }
    } catch (error) {
      console.error('Error fetching work items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReworks = async () => {
    try {
      console.log('Fetching reworks from /enquiry-reworks...');
      const response = await api.get('/enquiry-reworks');
      console.log('Reworks API response:', response.data);
      
      if (response.data.success) {
        console.log('Total reworks received:', response.data.data.length);
        // Only show pending reworks
        const pendingReworks = response.data.data.filter((r: Rework) => r.status === 'pending');
        console.log('Pending reworks after filtering:', pendingReworks.length, pendingReworks);
        setReworks(pendingReworks);
      } else {
        console.log('Reworks API returned success: false');
      }
    } catch (error: any) {
      console.error('Error fetching reworks:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleSelfAssign = async (enquiryId: number) => {
    try {
      const response = await api.post(`/work-assignments/self-assign/${enquiryId}`);
      
      if (response.data.success) {
        // Auto-start timer and redirect to work
        await api.post(`/work-assignments/${response.data.assignment_id}/start`);
        window.location.href = '/my-assignments';
      }
    } catch (error) {
      console.error('Error self-assigning:', error);
      alert('Error assigning to yourself. Please try again.');
    }
  };

  const handleSelfAssignRework = async (reworkId: number) => {
    try {
      const response = await api.post(`/rework-assignments/self-assign/${reworkId}`);
      
      if (response.data.success) {
        // Auto-start timer and redirect to work
        await api.post(`/work-assignments/${response.data.assignment_id}/start`);
        window.location.href = '/my-assignments';
      }
    } catch (error) {
      console.error('Error self-assigning rework:', error);
      alert('Error assigning rework to yourself. Please try again.');
    }
  };

  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const handleRefresh = () => {
    fetchEnquiries(); // This now fetches both enquiries and reworks
  };

  const fetchReworkHistory = async (enquiryId: number) => {
    try {
      setLoadingHistory(true);
      const response = await api.get(`/enquiry-reworks?enquiry_id=${enquiryId}`);
      if (response.data.success) {
        setReworkHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching rework history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-50 border-red-200';
      case 'high': return 'bg-orange-50 border-orange-200';
      case 'medium': return 'bg-yellow-50 border-yellow-200';
      case 'low': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getUrgencyScore = (enquiry: Enquiry) => {
    const priorityWeight = { urgent: 4, high: 3, medium: 2, low: 1 };
    const timeWeight = enquiry.hours_in_pool > 24 ? 2 : enquiry.hours_in_pool > 8 ? 1.5 : 1;
    return priorityWeight[enquiry.priority] * timeWeight;
  };



  const formatTimeInPool = (hours: number, createdAt: string) => {
    // Fallback: calculate time difference on client side if server calculation seems wrong
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const clientHours = diffMs / (1000 * 60 * 60);
    
    // Use client calculation if server calculation seems wrong (negative or too large)
    const actualHours = (hours < 0 || hours > 24) ? clientHours : hours;
    const absHours = Math.abs(actualHours);
    
    if (absHours < 1) {
      return `${Math.round(absHours * 60)}m`;
    } else if (absHours < 24) {
      return `${Math.round(absHours)}h`;
    } else {
      const days = Math.floor(absHours / 24);
      const remainingHours = Math.round(absHours % 24);
      return `${days}d ${remainingHours}h`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Available Work</h1>
              <p className="text-sm text-gray-600">Pick up pending travel requests and rework requests</p>
            </div>
            <div className="flex items-center space-x-4">
              {lastUpdated > 0 && (
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="h-4 w-4 mr-1" />
                  Updated {getTimeAgo(lastUpdated)}
                </div>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh Tasks'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Total Tasks</p>
                <p className="text-3xl font-bold">{enquiries.length + reworks.length}</p>
              </div>
              <Target className="h-10 w-10 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-teal-600 p-4 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">New Enquiries</p>
                <p className="text-3xl font-bold">{enquiries.length}</p>
              </div>
              <Mail className="h-10 w-10 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-red-500 p-4 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">🔄 Reworks</p>
                <p className="text-3xl font-bold">{reworks.length}</p>
              </div>
              <RefreshCw className="h-10 w-10 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-red-500 to-pink-600 p-4 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">🔥 Urgent</p>
                <p className="text-3xl font-bold">
                  {enquiries.filter(e => e.priority === 'urgent').length + reworks.length}
                </p>
              </div>
              <Zap className="h-10 w-10 opacity-80" />
            </div>
          </div>
        </div>

        {/* Combined Work Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Rework requests first (higher priority) */}
          {reworks
            .sort((a, b) => b.hours_in_pool - a.hours_in_pool)
            .map((rework) => (
            <div key={rework.rework_id} className="bg-white rounded-lg shadow-sm border-l-4 border-orange-500 p-4 hover:shadow-md transition-shadow">
              {/* Rework Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                    WN_{rework.enquiry_id}
                  </span>
                  <RefreshCw className="h-4 w-4 text-orange-600" />
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                    REWORK #{rework.rework_number}
                  </span>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                    HIGH PRIORITY
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-medium ${
                    rework.hours_in_pool > 24 ? 'text-red-600' :
                    rework.hours_in_pool > 8 ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                    {formatTimeInPool(rework.hours_in_pool, rework.created_at)}
                  </span>
                  <div className="text-xs text-gray-500">in pool</div>
                </div>
              </div>

              {/* Rework Content */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-1">{rework.agency_name}</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Original Enquiry #{rework.enquiry_id} • Travel: {new Date(rework.travel_date).toLocaleDateString('en-GB')}
                </p>
                
                {/* Changes Summary */}
                {(rework.pax_changed || rework.date_changed) && (
                  <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                    <p className="font-semibold text-orange-800 mb-1">Requested Changes:</p>
                    {rework.pax_changed && (
                      <p className="text-orange-700">• Passengers: {rework.new_adults}A, {rework.new_kids}K</p>
                    )}
                    {rework.date_changed && (
                      <p className="text-orange-700">
                        • New Date: {rework.new_travel_date ? new Date(rework.new_travel_date).toLocaleDateString('en-GB') : 'N/A'}
                      </p>
                    )}
                  </div>
                )}
                
                <p className="text-sm text-gray-700 line-clamp-2">
                  <span className="font-medium">Changes needed:</span> {rework.changes_requested}
                </p>
                {rework.selected_itinerary_name && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                    <p className="text-green-700">📋 Selected itinerary: {rework.selected_itinerary_name}</p>
                  </div>
                )}
                {rework.attachment_file_name && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center justify-between">
                    <p className="text-blue-700">📎 {rework.attachment_file_name}</p>
                    <button
                      onClick={() => {
                        const fileType = rework.attachment_file_name?.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
                        setFilePreview({ url: rework.attachment_file_path!, name: rework.attachment_file_name!, type: fileType });
                      }}
                      className="text-blue-600 hover:text-blue-800 underline ml-2"
                    >
                      View
                    </button>
                  </div>
                )}
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                  <p className="text-blue-700">📋 Original enquiry #{rework.enquiry_id} - check existing itineraries</p>
                </div>
              </div>

              {/* Rework Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedRework(rework)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View Details
                </button>
                <button
                  onClick={() => handleSelfAssignRework(rework.rework_id)}
                  className="flex-1 bg-orange-600 text-white py-2 px-3 rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm"
                >
                  🔄 Start Rework
                </button>
              </div>
            </div>
          ))}
          
          {/* Regular enquiries */}
          {enquiries
            .sort((a, b) => getUrgencyScore(b) - getUrgencyScore(a))
            .map((enquiry) => (
            <div key={enquiry.enquiry_id} className={`bg-white rounded-lg shadow-sm border-l-4 p-4 hover:shadow-md transition-shadow ${
              enquiry.priority === 'urgent' ? 'border-red-500' :
              enquiry.priority === 'high' ? 'border-orange-500' :
              enquiry.priority === 'medium' ? 'border-yellow-500' :
              'border-green-500'
            }`}>
              {/* Enquiry Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                    WN_{enquiry.enquiry_id}
                  </span>
                  {enquiry.source === 'whatsapp' ? (
                    <MessageSquare className="h-4 w-4 text-green-600" />
                  ) : (
                    <Mail className="h-4 w-4 text-blue-600" />
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    enquiry.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    enquiry.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    enquiry.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {enquiry.priority.toUpperCase()}
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-medium ${
                    enquiry.hours_in_pool > 24 ? 'text-red-600' :
                    enquiry.hours_in_pool > 8 ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                    {formatTimeInPool(enquiry.hours_in_pool, enquiry.created_at)}
                  </span>
                  <div className="text-xs text-gray-500">in pool</div>
                </div>
              </div>

              {/* Enquiry Content */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">{enquiry.agency_name}</h3>
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    <span className="text-blue-700 font-semibold text-sm">📅 {new Date(enquiry.travel_date).toLocaleDateString('en-GB')}</span>
                  </div>
                  {enquiry.number_of_adults !== undefined && (
                    <div className="flex items-center bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                      <span className="text-purple-700 font-semibold text-sm">👥 {enquiry.number_of_adults}A{enquiry.number_of_kids ? ` + ${enquiry.number_of_kids}K` : ''}</span>
                    </div>
                  )}
                  {enquiry.number_of_nights !== undefined && enquiry.number_of_nights > 0 && (
                    <div className="flex items-center bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5">
                      <span className="text-indigo-700 font-semibold text-sm">🌙 {enquiry.number_of_nights}N</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-700 line-clamp-3">
                  {enquiry.plan_text}
                </p>
              </div>

              {/* Enquiry Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedEnquiry(enquiry)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </button>
                <button
                  onClick={() => handleSelfAssign(enquiry.enquiry_id)}
                  className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  🚀 Start Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {(enquiries.length === 0 && reworks.length === 0) && (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Work</h3>
            <p className="text-gray-600">All requests have been assigned. Check back later!</p>
          </div>
        )}
      </div>

      {/* Quick View Modal */}
      {selectedEnquiry && (
        <EnquiryDetailsModal
          isOpen={true}
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
        />
      )}

      {/* Rework Quick View Modal */}
      {selectedRework && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onMouseEnter={() => fetchReworkHistory(selectedRework.enquiry_id)}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <RefreshCw className="h-5 w-5 text-orange-600" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedRework.agency_name}</h2>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                        REWORK #{selectedRework.rework_number}
                      </span>
                      <span className="text-xs text-gray-500">
                        WN_{selectedRework.enquiry_id}/{selectedRework.rework_number}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedRework(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Original Enquiry</label>
                    <p className="text-gray-900">#{selectedRework.enquiry_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Time in Pool</label>
                    <p className={`font-medium ${
                      selectedRework.hours_in_pool > 24 ? 'text-red-600' : 
                      selectedRework.hours_in_pool > 8 ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {formatTimeInPool(selectedRework.hours_in_pool, selectedRework.created_at)}
                    </p>
                  </div>
                </div>
                
                {/* Changes */}
                {(selectedRework.pax_changed || selectedRework.date_changed) && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <label className="block text-sm font-medium text-orange-800 mb-2">Requested Changes:</label>
                    {selectedRework.pax_changed && (
                      <p className="text-sm text-orange-700 mb-1">• Passengers: {selectedRework.new_adults}A, {selectedRework.new_kids}K</p>
                    )}
                    {selectedRework.date_changed && (
                      <p className="text-sm text-orange-700">
                        • Date: {selectedRework.new_travel_date ? new Date(selectedRework.new_travel_date).toLocaleDateString('en-GB') : 'N/A'}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Selected Itinerary */}
                {selectedRework.selected_itinerary_name && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">🎯 Itinerary to Rework</label>
                    <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                      <p className="text-green-900 font-bold text-lg">WH_{selectedRework.selected_itinerary_id}</p>
                      <p className="text-green-700 text-sm mt-1">{selectedRework.selected_itinerary_name}</p>
                      <p className="text-green-600 text-xs mt-2">⚠️ Make changes to this specific itinerary</p>
                    </div>
                  </div>
                )}
                
                {/* Attachment */}
                {selectedRework.attachment_file_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Attachment</label>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 flex items-center justify-between">
                      <p className="text-blue-800 font-medium">📎 {selectedRework.attachment_file_name}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            const fileType = selectedRework.attachment_file_name?.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
                            setFilePreview({ url: selectedRework.attachment_file_path!, name: selectedRework.attachment_file_name!, type: fileType });
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          <Eye className="h-4 w-4 inline mr-1" />
                          Preview
                        </button>
                        <a
                          href={selectedRework.attachment_file_path}
                          download={selectedRework.attachment_file_name}
                          className="text-green-600 hover:text-green-800 text-sm underline"
                        >
                          <Download className="h-4 w-4 inline mr-1" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Rework History */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">📋 Complete Request History</label>
                  {loadingHistory ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                    </div>
                  ) : reworkHistory.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {reworkHistory.map((item, index) => (
                        <div key={index} className={`p-3 rounded-lg border ${
                          item.rework_number === selectedRework.rework_number 
                            ? 'bg-orange-50 border-orange-300 border-2' 
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between mb-1">
                            <span className="font-semibold text-sm">
                              {item.rework_number === selectedRework.rework_number ? '➡️ ' : ''}
                              {item.request_type === 'fresh' ? '🆕 Original Request' : `🔄 Rework #${item.rework_number}`}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(item.created_at).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          {item.selected_itinerary_name && (
                            <p className="text-xs text-green-700 mb-1">📋 WH_{item.selected_itinerary_id}</p>
                          )}
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.changes_requested}</p>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                            item.status === 'completed' ? 'bg-green-100 text-green-700' :
                            item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 text-sm">
                      <p className="text-yellow-800">No history available</p>
                    </div>
                  )}
                </div>
                
                {/* Itineraries Section - Remove this since history is shown above */}
              </div>

              {/* Modal Actions */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setSelectedRework(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleSelfAssignRework(selectedRework.rework_id);
                    setSelectedRework(null);
                  }}
                  className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Assign to Me
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {filePreview && (
        <FileViewerModal
          isOpen={true}
          onClose={() => setFilePreview(null)}
          fileUrl={filePreview.url}
          fileName={filePreview.name}
          fileType={filePreview.type}
        />
      )}
    </div>
  );
};

export default WorkPool;